import type { PhaseType } from './types';

/**
 * Skupiny výrobních fází a jejich barvy. Vzniklo pro dílenskou tabuli a používá
 * se všude, kde se objeví typ fáze — aby „barvení" vypadalo stejně v tabuli,
 * ganttu, detailu výroby i v reportech.
 *
 * Barva jde od studené k teplé podle toho, kdo na fázi dělá: nejdřív se to
 * vymyslí u počítače (indigo), pak vyrobí strojem (cyan), pak dodělá rukou
 * (amber) a nakonec zabalí (emerald).
 *
 * Třídy jsou napsané celé, ne skládané za běhu — Tailwind generuje jen ty názvy,
 * které doslova najde ve zdroji.
 */
export type PhaseGroupId = 'navrh' | 'stroj' | 'ruka' | 'hotovo';

export interface PhaseGroupMeta {
  id: PhaseGroupId;
  label: string;
  text: string;
  borderTop: string;
  borderBottom: string;
  dot: string;
  badge: string;
}

export const PHASE_GROUPS: Record<PhaseGroupId, PhaseGroupMeta> = {
  navrh: {
    id: 'navrh',
    label: 'Návrh',
    text: 'text-indigo-600 dark:text-indigo-400',
    borderTop: 'border-t-indigo-500',
    borderBottom: 'border-b-indigo-500',
    dot: 'bg-indigo-500',
    badge: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300',
  },
  stroj: {
    id: 'stroj',
    label: 'Stroj',
    text: 'text-cyan-700 dark:text-cyan-400',
    borderTop: 'border-t-cyan-600 dark:border-t-cyan-400',
    borderBottom: 'border-b-cyan-600 dark:border-b-cyan-400',
    dot: 'bg-cyan-600 dark:bg-cyan-400',
    badge: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/40 dark:text-cyan-300',
  },
  ruka: {
    id: 'ruka',
    label: 'Ruka',
    text: 'text-amber-700 dark:text-amber-400',
    borderTop: 'border-t-amber-600 dark:border-t-amber-400',
    borderBottom: 'border-b-amber-600 dark:border-b-amber-400',
    dot: 'bg-amber-600 dark:bg-amber-400',
    badge: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  },
  hotovo: {
    id: 'hotovo',
    label: 'Dokončení',
    text: 'text-emerald-700 dark:text-emerald-400',
    borderTop: 'border-t-emerald-600 dark:border-t-emerald-400',
    borderBottom: 'border-b-emerald-600 dark:border-b-emerald-400',
    dot: 'bg-emerald-600 dark:bg-emerald-400',
    badge: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  },
};

// Do které skupiny fáze patří. Cenová nabídka se sice na dílně nedělá, ale je to
// příprava zakázky, takže barevně patří k návrhu.
export const PHASE_GROUP: Record<PhaseType, PhaseGroupId> = {
  cenova_nabidka: 'navrh',
  koncept: 'navrh',
  prototypovani: 'navrh',
  elektro_konzultace: 'navrh',
  finalni_rozkresleni: 'navrh',
  dokumentace: 'navrh',
  tisk_cnc_laser: 'stroj',
  postprocessing: 'stroj',
  barveni_patina: 'ruka',
  patina_doladeni: 'ruka',
  kompletace: 'ruka',
  foceni: 'hotovo',
  export: 'hotovo',
};

export const groupOf = (type: PhaseType): PhaseGroupMeta => PHASE_GROUPS[PHASE_GROUP[type]];
